import { Component, Loading, Pane3D, register } from 'rewild-ui';
import { Renderer, loadActivity } from 'rewild-renderer';
import { Player } from 'src/core/routing/Player';
import { GameManager } from 'src/core/GameManager';
import { GameLoadError } from './GameLoadError';
import { loadingLabel } from '../utils/loadingLabel';

interface Props {
  projectId: string;
  /** The level to play; the project's start level when unset. */
  levelId?: string;
  onTravel: (levelId: string) => void;
  onUnlock: () => void;
  onDeath: () => void;
  onQuit: () => void;
}

@register('x-viewport-statemachine')
export class ViewportStateMachine extends Component<Props> {
  gameManager: GameManager;
  player: Player;

  init() {
    this.player = new Player('Player');
    this.gameManager = new GameManager(this.player, this.props.onUnlock);
    this.player.onDeath = () => this.props.onDeath();
    this.gameManager.onTravel = (levelId) => this.props.onTravel(levelId);

    // Covers physics init, renderer setup and the initial level load.
    const [loading, setLoading] = this.useState(true);

    const onFrame = () => {
      this.gameManager.onUpdate();

      if (!this.gameManager.renderer.disposed)
        window.requestAnimationFrame(onFrame);
    };

    const onLoadActivity = (url: string) => {
      loadingOverlay.props = { overlay: true, label: loadingLabel(url) };
    };

    const onCanvasReady = async (pane3D: Pane3D) => {
      loadActivity.add(onLoadActivity);
      const initialized = await this.gameManager.init(
        pane3D,
        this.props.projectId,
        this.props.levelId
      );
      loadActivity.remove(onLoadActivity);
      this.gameManager.onUnlock = this.props.onUnlock;
      // init() swallows its own errors, so drop the overlay either way rather
      // than leaving the player staring at a shell that will never clear.
      setLoading(false);
      if (initialized) window.requestAnimationFrame(onFrame);
      else if (this.gameManager.loadError)
        toReturn.appendChild(
          <GameLoadError
            error={this.gameManager.loadError}
            onQuit={this.props.onQuit}
          />
        );
    };

    // Answers getActiveRenderer() the same way the editor viewport does, so UI
    // that touches the renderer — the settings panel — works in a game session
    // as well as in the editor.
    const onRequestRendererEvent = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        renderer: Renderer | null;
      };
      detail.renderer = this.gameManager.renderer;
    };

    this.onMount = () => {
      document.addEventListener('request-renderer', onRequestRendererEvent);
    };

    this.onCleanup = () => {
      document.removeEventListener('request-renderer', onRequestRendererEvent);
      loadActivity.remove(onLoadActivity);
    };

    const canvas = (<Pane3D onCanvasReady={onCanvasReady} />) as Pane3D;
    const loadingOverlay = (<Loading overlay label="Loading" />) as Loading;
    const toReturn = (
      <div class="container">
        {canvas}
      </div>
    ) as HTMLDivElement;

    return () => {
      if (loading()) {
        if (!loadingOverlay.parentElement) toReturn.appendChild(loadingOverlay);
      } else {
        loadingOverlay.remove();
      }
      return toReturn;
    };
  }

  getStyle() {
    return StyledInGame;
  }

  dispose() {
    this.gameManager.dispose();
  }
}

const StyledInGame = cssStylesheet(css`
  :host {
    width: 100vw;
    height: 100vh;
    display: block;
  }

  .container {
    width: 100%;
    height: 100%;
    position: relative;
  }
`);
